import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaWalking, FaRoad, FaFire } from "react-icons/fa";
import moment from "moment-timezone";
import "./Insights.css";
import openNotification from "components/utils/CustomNotification";
import { FaRegLightbulb } from "react-icons/fa";
import Modal from "react-modal";
import Lottie from "lottie-react";
import distanceJson from "../assets/distance.json";
import stepsJson from "../assets/steps.json";
import caloriesburned from "../assets/calories.json";
import healthtip from "../assets/HealthTip.json";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface AnimatedNumberProps {
  value: number;
  duration?: number;
  decimalPlaces?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  duration = 1500,
  decimalPlaces = 0,
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const progressRatio = Math.min(progress / duration, 1);
      setCount(parseFloat((progressRatio * value).toFixed(decimalPlaces)));
      if (progress < duration) requestAnimationFrame(step);
      else setCount(parseFloat(value.toFixed(decimalPlaces)));
    };

    requestAnimationFrame(step);
  }, [value, duration, decimalPlaces]);

  return <>{count}</>;
};

interface ProgressBarProps {
  percentage: number;
  className?: string;
}

const AnimatedProgressBar: React.FC<ProgressBarProps> = ({
  percentage,
  className,
}) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let start: number | null = null;

    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const progressRatio = Math.min(progress / 1500, 1);
      setWidth(parseFloat((progressRatio * percentage).toFixed(2)));
      if (progress < 1500) requestAnimationFrame(step);
      else setWidth(parseFloat(percentage.toFixed(2)));
    };

    requestAnimationFrame(step);
  }, [percentage]);

  return (
    <div className="insights-progress-bar">
      <div
        className={`insights-progress ${className}`}
        style={{ width: `${width}%` }}
      ></div>
    </div>
  );
};

const Insights: React.FC = () => {
  useCheckSessionHeartBeat();
  const [showFunFact, setShowFunFact] = useState(false);

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [selectedDate, setSelectedDate] = useState<[Date | null, Date | null]>([
    moment().tz(userTimeZone).subtract(7, "days").toDate(), // start
    new Date(), // end
  ]);

  const [selectedOption, setSelectedOption] = useState("space");
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState<any>(null);

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const getFunFactAnimation = () => {
    const metric = stats?.fun_fact?.metric;

    switch (metric) {
      case "steps":
        return stepsJson;

      case "distance":
        return distanceJson;

      case "caloriesburned":
        return caloriesburned;

      default:
        return healthtip;
    }
  };

  useEffect(() => {
    if (!selectedDate[0] || !selectedDate[1]) return;

    const fetchIndirectStats = async () => {
      try {
        setLoading(true);

        const formatDate = (date: Date) =>
          date.toISOString().split("T")[0] + "T00:00:00Z";

        if (!selectedDate[0] || !selectedDate[1]) return;

        const fromDate = formatDate(selectedDate[0]);
        const toDate = formatDate(selectedDate[1]);

        const response = await axios.get(`${beAddress}/api/indirect-stats`, {
          headers: {
            "user-uid": userUid,
          },
          params: {
            from_datetime: fromDate,
            to_datetime: toDate,
            theme: selectedOption,
          },
          withCredentials: true,
        });

        if (response.data.status === "Success") {
          setStats(response.data.data.metric_summary);
          setResponseData(response.data.data);
          // openNotification({
          //   type: "success",
          //   message: response.data.message || "Data fetched successfully!",
          // });
        } else {
          console.error(
            "Failed to fetch indirect stats:",
            response.data.message,
          );
          openNotification({
            type: "error",
            message: response.data.data || "Failed to fetch data",
          });
        }
      } catch (error) {
        console.error("Error fetching indirect stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIndirectStats();
  }, [selectedOption, selectedDate]);

  const calculateDaysDuration = (): number => {
    if (!responseData) return 0;

    try {
      const fromDate = new Date(responseData.from_datetime);
      const toDate = new Date(responseData.to_datetime);

      // Calculate difference in milliseconds
      const diffInMs = toDate.getTime() - fromDate.getTime();

      // Convert to days and round up to handle partial days
      const days = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

      // Ensure at least 1 day is returned
      return Math.max(1, days);
    } catch (error) {
      console.error("Error calculating days duration:", error);
      return 1;
    }
  };

  const getDurationDays = (): number => {
    if (!stats?.from_datetime || !stats?.to_datetime) {
      // Fallback to responseData or calculate from selectedDate
      if (responseData?.from_datetime && responseData?.to_datetime) {
        const fromDate = new Date(responseData.from_datetime);
        const toDate = new Date(responseData.to_datetime);
        const days = Math.ceil(
          (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24) + 1,
        );
        return Math.max(1, days);
      }
      return 1;
    }

    const fromDate = new Date(stats.from_datetime);
    const toDate = new Date(stats.to_datetime);
    const days = Math.ceil(
      (toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24) + 1,
    );
    return Math.max(1, days);
  };

  // Function to format milestone target values with 2 decimal places
  const formatMilestoneValue = (value: number, unit: string): string => {
    // Check if the value is a decimal number
    if (value % 1 !== 0) {
      return `${value.toFixed(2)} ${unit}`;
    }
    return `${value} ${unit}`;
  };

  return (
    <div className="insights-container">
      {/* Date Filters */}

      <div className="insights-date-filters">
        <div className="insights-date-box">
          <label className="insights-date-label">START DATE</label>
          <input
            type="date"
            className="insights-date-input"
            value={
              selectedDate[0] instanceof Date &&
              !isNaN(selectedDate[0].getTime())
                ? selectedDate[0].toISOString().split("T")[0]
                : ""
            }
            onChange={(e) =>
              setSelectedDate([
                e.target.value ? new Date(e.target.value) : null,
                selectedDate[1],
              ])
            }
            max={moment().tz(userTimeZone).format("YYYY-MM-DD")}
          />
        </div>

        <div className="insights-date-box">
          <label className="insights-date-label">END DATE</label>
          <input
            type="date"
            className="insights-date-input"
            value={
              selectedDate[1] instanceof Date &&
              !isNaN(selectedDate[1].getTime())
                ? selectedDate[1].toISOString().split("T")[0]
                : ""
            }
            onChange={(e) =>
              setSelectedDate([
                selectedDate[0],
                e.target.value ? new Date(e.target.value) : null,
              ])
            }
            max={moment().tz(userTimeZone).format("YYYY-MM-DD")}
          />
        </div>

        {/* Dropdown */}
        <select
          className="insights-dropdown"
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
        >
          <option value="space">Space</option>
          <option value="eco">Eco</option>
          <option value="fit">Fit</option>
          <option value="geo">Geo</option>
        </select>
      </div>

      {/* Summary Section */}
      {loading ? (
        <p>Loading data...</p>
      ) : (
        <>
          <div className="insights-summary">
            <h3>
              Summary for{" "}
              <span className="insights-highlight">
                {getDurationDays()} day{getDurationDays() !== 1 ? "s" : ""}
              </span>
            </h3>
            <p>Insights based on your steps</p>

            <div className="insights-summary-cards">
              <div className="insights-card insights-pink">
                <div className="insights-icon-bg pink">
                  <FaWalking size={24} />
                </div>
                <h2>
                  <AnimatedNumber
                    value={stats?.total_steps || 0}
                    decimalPlaces={0} // Steps are whole numbers
                  />
                </h2>
                <span>Total Steps</span>
              </div>
              <div className="insights-card insights-yellow">
                <div className="insights-icon-bg yellow">
                  <FaRoad size={24} />
                </div>
                <h2>
                  <AnimatedNumber
                    value={stats?.total_distance || 0}
                    decimalPlaces={2} // Distance can have decimals
                  />
                </h2>
                <span>Total Distance</span>
              </div>
              <div className="insights-card insights-green">
                <div className="insights-icon-bg green">
                  <FaFire size={24} />
                </div>
                <h2>
                  <AnimatedNumber
                    value={stats?.total_calories_burned || 0}
                    decimalPlaces={2} // Calories can have decimals
                  />
                </h2>
                <span>Total Calories</span>
              </div>
            </div>
          </div>

          {/* Milestones Section */}
          <div className="insights-milestones">
            <h3 className="insights-milestones-title">
              {selectedOption === "space"
                ? "Space Milestones"
                : selectedOption === "eco"
                  ? "Eco Milestones"
                  : selectedOption === "fit"
                    ? "Fit Milestones"
                    : "Geo Milestones"}

              {stats?.fun_fact?.description && (
                <FaRegLightbulb
                  size={22}
                  className="funfact-bulb"
                  onClick={() => setShowFunFact(true)}
                />
              )}
            </h3>

            <p>Achieved during this period of time</p>

            {stats?.milestones?.map((m: any, idx: number) => (
              <div className="insights-milestone-card" key={idx}>
                <div className="insights-milestone-header">
                  <h4>{m.title}</h4>
                  <span className="insights-milestone-value">
                    {formatMilestoneValue(m.target_value, m.unit)}
                  </span>
                </div>
                <AnimatedProgressBar
                  percentage={m.progress_percent}
                  className={m.title.toLowerCase().replace(/\s+/g, "-")}
                />
                <p>{m.message}</p>
              </div>
            ))}
          </div>
        </>
      )}
      <Modal
        isOpen={showFunFact}
        onRequestClose={() => setShowFunFact(false)}
        className="funfact-modal"
        overlayClassName="funfact-overlay"
      >
        <h2>Fun Fact</h2>
        {getFunFactAnimation() && (
          <Lottie
            animationData={getFunFactAnimation()}
            loop={true}
            className="funfact-animation"
          />
        )}
        <p>{stats?.fun_fact?.description}</p>

        <button className="close-btn" onClick={() => setShowFunFact(false)}>
          Close
        </button>
      </Modal>
    </div>
  );
};

export default Insights;
